import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { TrendingUp, Handshake, Trophy, Crown, Gift, Wallet, Banknote, Search } from 'lucide-react';

const incomeIcons = { roi: TrendingUp, direct: Handshake, level: Trophy, royalty: Crown, reward: Gift };
const incomeColors = { roi: 'var(--primary)', direct: '#22c55e', level: '#3b82f6', royalty: '#a855f7', reward: '#f59e0b' };

export default function AdminIncome() {
  const [incomes, setIncomes] = useState([]);
  const [totalsByType, setTotalsByType] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const fetchIncome = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (activeTab !== 'all') params.append('type', activeTab);
    if (search) params.append('search', search);
    api.get(`/income/admin/all?${params}`)
      .then(r => {
        setIncomes(r.data.incomes);
        setTotal(r.data.total);
        setTotalsByType(r.data.totalsByType || []);
        setGrandTotal(r.data.grandTotal || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeTab, page, search]);

  useEffect(() => { fetchIncome(); }, [fetchIncome]);

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const tabs = ['all', 'roi', 'direct', 'level', 'royalty', 'reward'];
  const totalPages = Math.ceil(total / LIMIT);

  const getTypeTotal = (type) => totalsByType.find(t => t._id === type)?.total || 0;
  const getTypeCount = (type) => totalsByType.find(t => t._id === type)?.count || 0;

  return (
    <DashboardLayout>
      <h1 className="page-title">All Transactions</h1>
      <p className="page-subtitle">Platform-wide income distribution across all users</p>

      {/* Summary Cards */}
      <div className="wallet-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'ROI Payout', key: 'roi', icon: TrendingUp, color: 'var(--primary)' },
          { label: 'Direct Income', key: 'direct', icon: Handshake, color: '#22c55e' },
          { label: 'Level Income', key: 'level', icon: Trophy, color: '#3b82f6' },
          { label: 'Royalty Income', key: 'royalty', icon: Crown, color: '#a855f7' },
          { label: 'Reward Income', key: 'reward', icon: Gift, color: '#f59e0b' },
          { label: 'Grand Total', key: null, icon: Wallet, color: '#22c55e' },
        ].map(w => (
          <div key={w.label} className="wallet-card">
            <div style={{ marginBottom: 6, color: w.color, display: 'flex', justifyContent: 'center' }}><w.icon size={24} /></div>
            <div className="wallet-label">{w.label}</div>
            <div className="wallet-value" style={{ color: w.color }}>
              {formatCurrency(w.key ? getTypeTotal(w.key) : grandTotal)}
            </div>
            {w.key && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{getTypeCount(w.key)} txns</div>}
          </div>
        ))}
      </div>

      {/* Income Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wallet size={18} /> All Transactions</h3>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Total: {total} records</div>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div className="form-group" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Search size={14} /> Search User</label>
            <input
              type="text"
              className="form-control"
              placeholder="Name, email, or referral code..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        <div className="tab-bar">
          {tabs.map(t => {
            const TabIcon = t !== 'all' ? incomeIcons[t] : null;
            return (
              <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`}
                onClick={() => { setActiveTab(t); setPage(1); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {TabIcon && <TabIcon size={14} />} {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : incomes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Banknote size={40} /></div>
            <h3>No income records found</h3>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead>
                <tr><th>Date</th><th>User</th><th>Type</th><th>Description</th><th>From</th><th>Level</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {incomes.map(inc => {
                  const RowIcon = incomeIcons[inc.type];
                  return (
                    <tr key={inc._id}>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(inc.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{inc.user?.name || '—'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inc.user?.referralCode}</div>
                      </td>
                      <td>
                        <span className="badge" style={{ background: `${incomeColors[inc.type]}18`, color: incomeColors[inc.type], border: `1px solid ${incomeColors[inc.type]}40`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <RowIcon size={12} /> {inc.type.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{inc.description || '—'}</td>
                      <td style={{ fontSize: '0.85rem' }}>{inc.fromUser?.name || '—'}</td>
                      <td>{inc.level > 0 ? `L${inc.level}` : '—'}</td>
                      <td style={{ color: '#22c55e', fontWeight: 700 }}>+{formatCurrency(inc.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const p = i + Math.max(1, page - 2);
              if (p > totalPages) return null;
              return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
            })}
            <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}